import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import axios from 'axios';
import Cookies from 'js-cookie';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';
import { usePermissions } from '../../providers/permissions-provider';
import { useToast } from '../../providers/toaster-provider';
import { formatHorizon, useSubscriptionTiers } from '../../util/subscription-tiers';
import UpgradeRequiredDialog from './upgrade-required-dialog';
import { AddonsCheckout, AddonState } from './subscription-plans';

type BeyondHorizon = {
    count: number;
    horizonDays?: number;
    gate?: { action: 'upgrade_required' | 'addon_required'; error?: string; toTierId?: string; qualifyingTierIds?: string[]; addonKey?: string } | null;
};

type Stage = 'idle' | 'upgrade' | 'addon' | 'notice';

// After a Xero invoice sync: invoices dated past the company's projection
// horizon aren't imported (xero-import-service.js splitByHorizon). When some
// were held back, the upgrade / horizon add-on prompt opens straight away; once
// the horizon is extended the import continues for them (from the data already
// pulled from Xero). Closing the prompt without extending says how many weren't
// imported. Call checkInvoiceHorizon() when an invoice sync finishes.
export const useInvoiceHorizonCheck = () => {
    const { user } = useUser();
    const { isAdmin } = usePermissions();
    const { showSuccess, showError } = useToast();
    const { tiers } = useSubscriptionTiers();
    const [result, fetchResult] = useApi<BeyondHorizon & { success: boolean }>();
    const [status, fetchStatus] = useApi<{ tierId: string; addons?: AddonState[] }>();
    const [stage, setStage] = React.useState<Stage>('idle');
    // The horizon was extended in the prompt - closing it then isn't a "no".
    const extendedRef = React.useRef(false);

    const checkInvoiceHorizon = React.useCallback(() => {
        if (!user?.entityId) return;
        fetchResult(buildApiRequestConfig(routes.xero.invoicesBeyondHorizon, [{ param: 'entityId', value: user.entityId }]));
    }, [user?.entityId, fetchResult]);

    const data = result.data;
    const count = data?.count ?? 0;
    const gate = data?.gate ?? null;
    const horizonText = data?.horizonDays ? formatHorizon(data.horizonDays) : '';
    const countText = `${count} invoice${count === 1 ? ' is' : 's are'} dated past your ${horizonText} projection horizon`;

    React.useEffect(() => {
        if (!data?.success || count === 0) return;
        extendedRef.current = false;
        if (gate?.action === 'upgrade_required' && gate.toTierId) {
            setStage('upgrade');
        } else if (gate?.action === 'addon_required' && user?.entityId) {
            fetchStatus(buildApiRequestConfig(routes.subscription.fetchStatus, [{ param: 'entityId', value: user.entityId }]));
            setStage('addon');
        } else {
            setStage('notice');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const continueImport = async () => {
        if (!user?.entityId) return;
        extendedRef.current = true;
        const { url, method } = buildApiRequestConfig(routes.xero.continueInvoiceImport, [{ param: 'entityId', value: user.entityId }]);
        const token = Cookies.get('token');
        try {
            await axios({ url, method, data: {}, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
            showSuccess(`Importing ${count} more invoice${count === 1 ? '' : 's'}`);
        } catch (err: any) {
            showError(err?.response?.data?.error || 'Failed to continue the invoice import');
        }
    };

    // Closed without extending the horizon - say what wasn't imported.
    const closePrompt = () => setStage(extendedRef.current ? 'idle' : 'notice');

    const currentTier = tiers.find(t => t.id === status.data?.tierId) ?? null;

    const invoiceHorizonDialog = (
        <>
            {stage === 'upgrade' && user?.entityId && gate?.toTierId && (
                <UpgradeRequiredDialog
                    open
                    onClose={closePrompt}
                    entityId={user.entityId}
                    toTierId={gate.toTierId}
                    qualifyingTierIds={gate.qualifyingTierIds}
                    message={`${countText}. ${gate.error ?? ''}`.trim()}
                    onUpgraded={continueImport}
                />
            )}
            {stage === 'addon' && (
                <AddonsCheckout
                    open={!!currentTier}
                    onClose={closePrompt}
                    plan={currentTier}
                    addons={status.data?.addons ?? []}
                    readOnly={!isAdmin}
                    preselect={gate?.addonKey ? [gate.addonKey] : []}
                    onDone={() => {}}
                    onApplied={continueImport}
                />
            )}
            <Dialog open={stage === 'notice' && count > 0} onClose={() => setStage('idle')} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={600}>Some invoices weren't imported</DialogTitle>
                <DialogContent>
                    <Typography fontSize={14} color="var(--color-text-muted)">
                        {countText} and {count === 1 ? "wasn't" : "weren't"} imported.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setStage('idle')} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );

    return { checkInvoiceHorizon, invoiceHorizonDialog };
};
